import { useState } from 'react'
import { VStack, HStack, Card, Text, Heading, Badge, Button, Modal, Input, Skeleton, useToast } from 'wss3-forge'
import {
  Fingerprint20Regular,
  Key20Regular,
  Delete20Regular,
  Edit20Regular,
  Add20Regular,
  Checkmark20Regular
} from '@fluentui/react-icons'
import { usePasskeys } from '../../hooks/useSecurity'
import type { Passkey } from '../../types/database'

interface PasskeysCardProps {
  passkeys: Passkey[]
  loading: boolean
  onDelete: (id: string) => Promise<{ error: Error | null }>
  onRename: (id: string, name: string) => Promise<{ error: Error | null }>
  onReload: () => void
}

function formatDate(date: string | null): string {
  if (!date) return 'Never'
  const d = new Date(date)
  return d.toLocaleDateString()
}

// Convert base64url to standard base64 for atob()
function base64urlToBase64(base64url: string): string {
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  // Add padding if needed
  while (base64.length % 4) {
    base64 += '='
  }
  return base64
}

export function PasskeysCard({ passkeys, loading, onDelete, onRename, onReload }: PasskeysCardProps) {
  const toast = useToast()
  const {
    isSupported,
    checkPlatformAuthenticator,
    getRegistrationOptions,
    completeRegistration
  } = usePasskeys()

  const [showAddModal, setShowAddModal] = useState(false)
  const [showRenameModal, setShowRenameModal] = useState(false)
  const [selectedPasskey, setSelectedPasskey] = useState<Passkey | null>(null)
  const [newName, setNewName] = useState('')
  const [registering, setRegistering] = useState(false)
  const [hasPlatformAuth, setHasPlatformAuth] = useState<boolean | null>(null)

  const openAddModal = () => {
    setNewName('')
    setShowAddModal(true)
    setRegistering(false)
  }

  const handleStartRegistration = async () => {
    setRegistering(true)

    try {
      // Check platform authenticator availability
      const platformAvailable = await checkPlatformAuthenticator()
      setHasPlatformAuth(platformAvailable)

      // Get registration options from server
      const { data: options, error } = await getRegistrationOptions()
      if (error) throw error

      // Create credential using WebAuthn API
      const credential = await navigator.credentials.create({
        publicKey: {
          ...options,
          challenge: Uint8Array.from(atob(base64urlToBase64(options.challenge)), c => c.charCodeAt(0)),
          user: {
            ...options.user,
            id: Uint8Array.from(atob(base64urlToBase64(options.user.id)), c => c.charCodeAt(0))
          },
          excludeCredentials: options.excludeCredentials?.map((cred: any) => ({
            ...cred,
            id: Uint8Array.from(atob(base64urlToBase64(cred.id)), c => c.charCodeAt(0))
          }))
        }
      }) as PublicKeyCredential

      if (!credential) {
        throw new Error('Failed to create credential')
      }

      // Complete registration
      const { error: verifyError } = await completeRegistration(credential, newName || undefined)
      if (verifyError) throw verifyError

      toast.success('Passkey added successfully!')
      setShowAddModal(false)
      setNewName('')
      onReload()
    } catch (err: any) {
      console.error('Error registering passkey:', err)
      if (err.name === 'NotAllowedError') {
        toast.error('Registration was cancelled or timed out')
      } else {
        toast.error(err.message || 'Failed to register passkey')
      }
    } finally {
      setRegistering(false)
    }
  }

  const handleRename = async () => {
    if (!selectedPasskey || !newName.trim()) return

    const { error } = await onRename(selectedPasskey.id, newName.trim())
    if (error) {
      toast.error('Failed to rename passkey')
    } else {
      toast.success('Passkey renamed')
      setShowRenameModal(false)
      setSelectedPasskey(null)
      setNewName('')
    }
  }

  const handleDelete = async (passkey: Passkey) => {
    if (!confirm(`Delete passkey "${passkey.name || 'Unnamed'}"?`)) return

    const { error } = await onDelete(passkey.id)
    if (error) {
      toast.error('Failed to delete passkey')
    } else {
      toast.success('Passkey deleted')
    }
  }

  const openRenameModal = (passkey: Passkey) => {
    setSelectedPasskey(passkey)
    setNewName(passkey.name || '')
    setShowRenameModal(true)
  }

  if (loading) {
    return (
      <Card padding="md">
        <VStack gap="md">
          <HStack gap="sm" style={{ alignItems: 'center' }}>
            <Fingerprint20Regular style={{ color: 'var(--brand-primary)' }} />
            <Heading level={5}>Passkeys</Heading>
          </HStack>
          <VStack gap="sm">
            <Skeleton width="100%" height={60} />
            <Skeleton width="100%" height={40} />
          </VStack>
        </VStack>
      </Card>
    )
  }

  return (
    <>
      <Card padding="md">
        <VStack gap="md">
          <HStack gap="sm" style={{ alignItems: 'center', marginBottom: '0.5rem' }}>
            <Fingerprint20Regular style={{ color: 'var(--brand-primary)' }} />
            <Heading level={5}>Passkeys</Heading>
            {!isSupported && (
              <Badge variant="warning">Not supported</Badge>
            )}
          </HStack>

          <Text size="sm" color="muted">
            Passkeys let you sign in without a password using Face ID, Touch ID, Windows Hello, or a security key.
          </Text>

          {!isSupported ? (
            <Card variant="outlined" padding="sm">
              <Text size="sm" color="muted">
                Your browser doesn't support passkeys. Try using a modern browser like Chrome, Safari, or Edge.
              </Text>
            </Card>
          ) : passkeys.length === 0 ? (
            <Card variant="outlined" padding="md" style={{ textAlign: 'center' }}>
              <VStack gap="sm" style={{ alignItems: 'center' }}>
                <Key20Regular style={{ width: 32, height: 32, color: 'var(--text-muted)' }} />
                <Text color="muted">No passkeys registered</Text>
                <Button variant="primary" icon={<Add20Regular />} onClick={openAddModal}>
                  Add your first passkey
                </Button>
              </VStack>
            </Card>
          ) : (
            <VStack gap="sm">
              {passkeys.map((passkey) => (
                <div
                  key={passkey.id}
                  style={{
                    padding: '0.75rem',
                    backgroundColor: 'var(--bg-secondary)',
                    borderRadius: '8px'
                  }}
                >
                  <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <Fingerprint20Regular style={{ color: 'var(--brand-primary)' }} />
                      <VStack gap="xs">
                        <Text size="sm" weight="medium">
                          {passkey.name || 'Unnamed passkey'}
                        </Text>
                        <HStack gap="sm">
                          <Text size="xs" color="muted">
                            Added {formatDate(passkey.created_at)}
                          </Text>
                          {passkey.last_used_at && (
                            <Text size="xs" color="muted">
                              Last used {formatDate(passkey.last_used_at)}
                            </Text>
                          )}
                        </HStack>
                      </VStack>
                    </HStack>
                    <HStack gap="xs">
                      <Badge size="sm" variant="default">
                        {passkey.device_type === 'platform' ? 'Built-in' : 'Security key'}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Edit20Regular />}
                        onClick={() => openRenameModal(passkey)}
                      >{''}</Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Delete20Regular />}
                        onClick={() => handleDelete(passkey)}
                      >{''}</Button>
                    </HStack>
                  </HStack>
                </div>
              ))}
            </VStack>
          )}

          {isSupported && passkeys.length > 0 && (
            <HStack gap="sm" style={{ justifyContent: 'flex-end' }}>
              <Button
                variant="secondary"
                size="sm"
                icon={<Add20Regular />}
                onClick={openAddModal}
              >
                Add
              </Button>
            </HStack>
          )}
        </VStack>
      </Card>

      {/* Add Passkey Modal */}
      <Modal
        open={showAddModal}
        onClose={() => !registering && setShowAddModal(false)}
        title="Add Passkey"
      >
        <VStack gap="md">
          {registering ? (
            <VStack gap="md" style={{ alignItems: 'center', padding: '2rem' }}>
              <Fingerprint20Regular style={{ width: 48, height: 48, color: 'var(--brand-primary)' }} />
              <Text>Follow the prompts from your browser or device...</Text>
              <Text size="sm" color="muted">
                {hasPlatformAuth
                  ? 'Use Touch ID, Face ID, or Windows Hello to register'
                  : 'Insert and activate your security key'}
              </Text>
            </VStack>
          ) : (
            <>
              <Text>
                Give your passkey a name to help you identify it later.
              </Text>
              <Input
                label="Passkey name (optional)"
                placeholder="e.g., MacBook Touch ID"
                value={newName}
                onChange={(v) => setNewName(v as string)}
              />
              <Button
                variant="primary"
                icon={<Fingerprint20Regular />}
                onClick={handleStartRegistration}
                fullWidth
              >
                Register passkey
              </Button>
            </>
          )}
        </VStack>
      </Modal>

      {/* Rename Modal */}
      <Modal
        open={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        title="Rename Passkey"
      >
        <VStack gap="md">
          <Input
            label="Passkey name"
            value={newName}
            onChange={(v) => setNewName(v as string)}
            placeholder="Enter a name"
          />
          <HStack gap="sm" style={{ justifyContent: 'flex-end' }}>
            <Button variant="ghost" onClick={() => setShowRenameModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              icon={<Checkmark20Regular />}
              onClick={handleRename}
              disabled={!newName.trim()}
            >
              Save
            </Button>
          </HStack>
        </VStack>
      </Modal>
    </>
  )
}
